// Mix-Graph: Kanalzüge mit Insert-Ketten, Sends auf Aux-Busse (Hall, Delay), Master mit Inserts und Lautheitsmessung.
// Wird live (AudioContext) und beim Export (OfflineAudioContext) gleich gebaut, damit beides identisch klingt.
//
// Kanalzug:  Quelle → input → [Insert A … J] → Pan → Fader → Summe
//                                                    └→ Send (post Fader) → Bus
// Master:    Summe → (alter Mix-Bus) → [Master-Inserts] → Master-Fader → Abhörkontrolle → Ausgang
import type { InsertSlot, Project } from '@srs/shared';
import { pluginDef, resolveParams, bussesOf, type PluginInstance, type PluginEnv } from './plugins.ts';
import { insOverrides, isAutomated, masterTrack, points, samples, valueAt } from '../edit/automation.ts';
import { isFrozen } from '../edit/mixops.ts';
import { browserInserts, rendered as fxRendered } from './fxstate.ts';

/** Automationszustand für sync(): läuft die Wiedergabe, wo steht sie, welche Parameter steuert gerade die Hand */
export interface AutoState { playing: boolean; pos: number; manual: Set<string> }
const NO_AUTO: AutoState = { playing: false, pos: 0, manual: new Set() };
const autoLin = (db: number) => (db <= -59.9 ? 0 : Math.pow(10, db / 20));

export const dbToGain = (db: number) => (db <= -96 ? 0 : Math.pow(10, db / 20));

interface ChainItem { id: string; plugin: string; inst: PluginInstance; tail: GainNode; key: string }

/** Insert-Kette eines Kanals */
export class Chain {
  readonly input: GainNode;
  readonly output: GainNode;
  items: ChainItem[] = [];
  private sig = '';
  /**
   * Mono-Quelle vor der ersten Insert-Stelle mit gleicher Leistung auf Stereo verteilen (−3 dB je Seite, wie der Panner).
   * Ohne das würde jede Insert-Kette eine Mono-Spur um 3 dB lauter machen (L = R = voller Pegel). Stereo bleibt unverändert.
   */
  private up: StereoPannerNode;
  constructor(private ctx: BaseAudioContext) {
    this.input = ctx.createGain();
    this.output = ctx.createGain();
    this.up = ctx.createStereoPanner();
    this.input.connect(this.output);
  }
  /** ov: automatisierte Parameter je Slot, überschreiben die gespeicherten Werte */
  sync(slots: InsertSlot[], env: PluginEnv, ov?: Record<string, Record<string, number>>) {
    const keep: ChainItem[] = [];
    for (const s of slots) {
      const def = pluginDef(s.plugin);
      if (!def) continue;
      let it = this.items.find((x) => x.id === s.id && x.plugin === s.plugin);
      const params = ov?.[s.id] ? { ...resolveParams(def, s.params), ...ov[s.id] } : resolveParams(def, s.params);
      const key = JSON.stringify(params) + env.bpm;
      if (!it) {
        const inst = def.create(this.ctx, params, env);
        const tail = this.ctx.createGain();
        inst.output.connect(tail);
        it = { id: s.id, plugin: s.plugin, inst, tail, key };
      } else if (it.key !== key) { it.inst.set(params, env); it.key = key; }
      keep.push(it);
    }
    for (const old of this.items) if (!keep.includes(old)) { old.tail.disconnect(); old.inst.dispose(); }
    this.items = keep;
    const sig = slots.filter((s) => pluginDef(s.plugin)).map((s) => `${s.id}:${s.plugin}:${s.bypass ? 1 : 0}`).join('|');
    if (sig !== this.sig) {
      this.sig = sig;
      this.input.disconnect(); this.up.disconnect();
      for (const it of this.items) it.tail.disconnect();
      let prev: AudioNode = this.input;
      if (this.items.some((it) => !slots.find((s) => s.id === it.id)?.bypass)) { this.input.connect(this.up); prev = this.up; }
      for (const it of this.items) {
        if (slots.find((s) => s.id === it.id)?.bypass) continue;
        prev.connect(it.inst.input); prev = it.tail;
      }
      prev.connect(this.output);
    }
  }
  instance(slotId: string) { return this.items.find((x) => x.id === slotId)?.inst; }
  dispose() { this.input.disconnect(); this.up.disconnect(); this.output.disconnect(); for (const it of this.items) { it.tail.disconnect(); it.inst.dispose(); } this.items = []; }
}

/**
 * Panorama mit gleicher Leistung für Mono-Inhalt, auch wenn eine Insert-Kette ihn schon auf L = R verteilt hat
 * (Web-Audio-StereoPanner würde dann nur balancieren und beim Drehen bis zu 3 dB lauter werden).
 *  - Mono und L = R: wie ein Mono-Panner (cos/sin, −3 dB in der Mitte), gleiche Leistung in jeder Stellung
 *  - Stereo in der Mitte: unverändert; zur Seite gedreht wird die Gegenseite gedämpft und ein Teil davon übernommen
 * Der Wert liegt als AudioParam vor (pan.pan) und lässt sich wie bisher automatisieren; die Koeffizienten folgen ihm
 * über Kennlinien sampelgenau.
 */
export class Panner {
  readonly input: StereoPannerNode;
  readonly output: ChannelMergerNode;
  readonly pan: AudioParam;
  private src: ConstantSourceNode;
  constructor(ctx: BaseAudioContext) {
    this.input = ctx.createStereoPanner(); // Mono → L = R = −3 dB, Stereo unverändert
    const split = ctx.createChannelSplitter(2);
    this.output = ctx.createChannelMerger(2);
    this.src = ctx.createConstantSource(); this.src.offset.value = 0;
    this.pan = this.src.offset;
    const N = 1025, curve = (f: (p: number) => number) => Float32Array.from({ length: N }, (_, i) => f(-1 + (2 * i) / (N - 1)));
    const th = (p: number) => ((Math.max(-1, Math.min(1, p)) + 1) * Math.PI) / 4;
    const k: [number, number, (p: number) => number][] = [
      [0, 0, (p) => (p > 0 ? Math.SQRT2 * Math.cos(th(p)) : 1)],     // L → L
      [0, 1, (p) => (p > 0 ? Math.SQRT2 * Math.sin(th(p)) - 1 : 0)], // L → R
      [1, 1, (p) => (p < 0 ? Math.SQRT2 * Math.sin(th(p)) : 1)],     // R → R
      [1, 0, (p) => (p < 0 ? Math.SQRT2 * Math.cos(th(p)) - 1 : 0)], // R → L
    ];
    this.input.connect(split);
    for (const [from, to, f] of k) {
      const g = ctx.createGain(); g.gain.value = 0;
      const sh = ctx.createWaveShaper(); sh.curve = curve(f);
      this.src.connect(sh).connect(g.gain);
      split.connect(g, from).connect(this.output, 0, to);
    }
    this.src.start();
  }
  connect<T extends AudioNode>(n: T): T { return this.output.connect(n) as T; }
  disconnect() { this.output.disconnect(); }
  dispose() { try { this.src.stop(); } catch { /* schon gestoppt */ } this.input.disconnect(); this.output.disconnect(); }
}

export interface StripNodes {
  id: string;
  input: GainNode;
  chain: Chain;
  pan: { pan: AudioParam };
  /** Automation: Mute und Lautstärke vor dem Fader (Sends hängen dahinter und folgen damit) */
  muteGain: GainNode;
  autoGain: GainNode;
  fader: GainNode;
  /** Summe beider Kanäle für kleine Pegelanzeigen */
  meter: AnalyserNode; meterBuf: Float32Array;
  aL: AnalyserNode; aR: AnalyserNode; bufL: Float32Array; bufR: Float32Array;
  sends: Map<string, GainNode>;
  /** Ausgang des Kanalzugs (nach dem Fader) und wohin er gerade geht: Master-Summe oder ein Aux-Bus */
  out: GainNode; dest: AudioNode;
}

export interface Loudness { m: number; s: number; i: number; peak: number; maxPeak: number }

export class MixGraph {
  strips = new Map<string, StripNodes>();
  busses = new Map<string, StripNodes>();
  sum!: GainNode; legacyEq!: BiquadFilterNode; legacyComp!: DynamicsCompressorNode;
  masterChain!: Chain; masterFader!: GainNode; phoneHP!: BiquadFilterNode; phoneLP!: BiquadFilterNode; monoNode!: GainNode; out!: GainNode;
  master!: StripNodes;
  loud: AudioWorkletNode | null = null;
  loudness: Loudness = { m: -Infinity, s: -Infinity, i: -Infinity, peak: -Infinity, maxPeak: -Infinity };
  workletOk = false;

  constructor(readonly ctx: BaseAudioContext, private live: boolean) {}

  async init() {
    const ctx = this.ctx;
    try { await ctx.audioWorklet.addModule('/dsp-worklet.js'); this.workletOk = true; } catch { this.workletOk = false; }
    this.sum = ctx.createGain();
    this.legacyEq = ctx.createBiquadFilter(); this.legacyEq.type = 'peaking'; this.legacyEq.gain.value = 0;
    this.legacyComp = ctx.createDynamicsCompressor(); this.legacyComp.threshold.value = 0; this.legacyComp.ratio.value = 1; this.legacyComp.attack.value = 0.01; this.legacyComp.release.value = 0.2;
    this.masterChain = new Chain(ctx);
    this.masterFader = ctx.createGain();
    this.phoneHP = ctx.createBiquadFilter(); this.phoneHP.type = 'highpass'; this.phoneHP.frequency.value = 10;
    this.phoneLP = ctx.createBiquadFilter(); this.phoneLP.type = 'lowpass'; this.phoneLP.frequency.value = 22000;
    this.monoNode = ctx.createGain();
    this.out = ctx.createGain();
    this.phoneHP.Q.value = -3.01; this.phoneLP.Q.value = -3.01;
    this.legacyEq.connect(this.legacyComp);
    this.phoneHP.connect(this.phoneLP);
    this.masterChain.output.connect(this.masterFader);
    this.monoNode.connect(this.out);
    this.route(false, false);
    this.out.connect(ctx.destination);
    // Master-Messung
    const meter = ctx.createAnalyser(); meter.fftSize = 2048;
    const { aL, aR } = this.stereoMeter(this.out);
    this.out.connect(meter);
    this.master = { id: 'master', input: this.sum, chain: this.masterChain, pan: ctx.createStereoPanner(), muteGain: ctx.createGain(), autoGain: ctx.createGain(), fader: this.masterFader, meter, meterBuf: new Float32Array(2048), aL, aR, bufL: new Float32Array(2048), bufR: new Float32Array(2048), sends: new Map(), out: this.out, dest: ctx.destination };
    if (this.live && this.workletOk) {
      try {
        this.loud = new AudioWorkletNode(ctx, 'srs-loud', { numberOfInputs: 1, numberOfOutputs: 0, channelCount: 2, channelCountMode: 'explicit' });
        this.loud.port.onmessage = (e) => { this.loudness = e.data as Loudness; };
        this.out.connect(this.loud);
      } catch { this.loud = null; }
    }
  }

  private legacyOn: boolean | null = null; private phoneOn: boolean | null = null;
  /** Alter Mix-Bus und Handy-Abhöre hängen nur im Signalweg, wenn sie gebraucht werden: sonst exakt neutral nach dem Limiter */
  private route(legacy: boolean, phone: boolean) {
    if (legacy !== this.legacyOn) {
      this.sum.disconnect(); this.legacyComp.disconnect();
      if (legacy) { this.sum.connect(this.legacyEq); this.legacyComp.connect(this.masterChain.input); }
      else this.sum.connect(this.masterChain.input);
      this.legacyOn = legacy;
    }
    if (phone !== this.phoneOn) {
      this.masterFader.disconnect(); this.phoneLP.disconnect();
      if (phone) { this.masterFader.connect(this.phoneHP); this.phoneLP.connect(this.monoNode); }
      else this.masterFader.connect(this.monoNode);
      this.phoneOn = phone;
    }
  }

  private stereoMeter(src: AudioNode) {
    const split = this.ctx.createChannelSplitter(2);
    const aL = this.ctx.createAnalyser(), aR = this.ctx.createAnalyser();
    aL.fftSize = 2048; aR.fftSize = 2048;
    src.connect(split); split.connect(aL, 0); split.connect(aR, 1);
    return { aL, aR };
  }

  private makeStrip(id: string, dest: AudioNode): StripNodes {
    const ctx = this.ctx;
    const input = ctx.createGain();
    const chain = new Chain(ctx);
    const pan = new Panner(ctx);
    const muteGain = ctx.createGain(), autoGain = ctx.createGain();
    const fader = ctx.createGain();
    const meter = ctx.createAnalyser(); meter.fftSize = 1024;
    const out = ctx.createGain();
    input.connect(chain.input);
    chain.output.connect(pan.input); pan.connect(muteGain).connect(autoGain).connect(fader).connect(out).connect(dest);
    fader.connect(meter);
    const { aL, aR } = this.live ? this.stereoMeter(fader) : { aL: meter, aR: meter };
    return { id, input, chain, pan, muteGain, autoGain, fader, meter, meterBuf: new Float32Array(1024), aL, aR, bufL: new Float32Array(2048), bufR: new Float32Array(2048), sends: new Map(), out, dest };
  }

  /** Kanalzug einer Spur (wird bei Bedarf angelegt) */
  strip(trackId: string): StripNodes {
    let s = this.strips.get(trackId);
    if (!s) { s = this.makeStrip(trackId, this.sum); this.strips.set(trackId, s); }
    return s;
  }
  private bus(id: string): StripNodes {
    let s = this.busses.get(id);
    if (!s) { s = this.makeStrip(id, this.sum); this.busses.set(id, s); }
    return s;
  }
  /** Kanal nach Id: Spur, Bus oder 'master' */
  channel(id: string): StripNodes | undefined { return id === 'master' ? this.master : this.strips.get(id) ?? this.busses.get(id); }

  private param(a: AudioParam, v: number) {
    if (this.live && (this.ctx as AudioContext).state === 'running') a.setTargetAtTime(v, this.ctx.currentTime, 0.015);
    else a.value = v;
  }

  /** Projektzustand auf die Knoten übertragen */
  sync(p: Project, monitor: { phone: boolean; mono: boolean }, auto: AutoState = NO_AUTO) {
    const env: PluginEnv = { bpm: p.bpm, offline: !this.live };
    const busses = bussesOf(p);
    const anySolo = p.tracks.some((t) => t.solo);
    for (const b of busses) {
      const s = this.bus(b.id);
      // Eigene Plugins auf dem Bus gerechnet: die Datei ersetzt die Summe der Sends (Eingang stumm, Quelle direkt in die Kette)
      s.chain.sync(browserInserts(p, b, false), env);
      this.param(s.input.gain, fxRendered(p, b) ? 0 : 1);
      this.param(s.fader.gain, b.mute ? 0 : dbToGain(b.volumeDb));
      this.param(s.pan.pan, b.pan);
    }
    for (const [id, s] of this.busses) if (!busses.some((b) => b.id === id)) { s.out.disconnect(); s.chain.dispose(); (s.pan as Panner).dispose?.(); this.busses.delete(id); }
    for (const t of p.tracks) {
      if (t.kind === 'bus') continue;
      const s = this.strip(t.id);
      // Eingefroren: Inserts sind schon in der gerechneten Fassung, die Kette bleibt leer (spart Rechenzeit)
      s.chain.sync(isFrozen(p, t) ? [] : browserInserts(p, t, this.live), env, insOverrides(t, auto.pos, auto.manual));
      // Ausgang: Aux-Bus (Submix) oder Master
      const dest = t.output && busses.some((b) => b.id === t.output) ? this.bus(t.output).input : this.sum;
      if (s.dest !== dest) { s.out.disconnect(); s.out.connect(dest); s.dest = dest; }
      // Automation: Read-Parameter laufen während der Wiedergabe über die eingeplanten Kurven, im Stillstand gilt der Wert an der Abspielposition
      const A = (param: string) => isAutomated(t, param) && !auto.manual.has(`${t.id}|${param}`);
      const at = (param: string) => valueAt(points(t, param), auto.pos, param, 0);
      const muteAuto = A('mute');
      const audible = (muteAuto || !t.mute) && (!anySolo || t.solo);
      const volAuto = A('volume');
      this.param(s.fader.gain, audible ? (volAuto ? 1 : dbToGain(t.volumeDb)) : 0);
      if (!volAuto) this.param(s.autoGain.gain, 1); else if (!auto.playing) this.param(s.autoGain.gain, autoLin(at('volume')));
      if (!muteAuto) this.param(s.muteGain.gain, 1); else if (!auto.playing) this.param(s.muteGain.gain, at('mute') >= 0.5 ? 0 : 1);
      if (isFrozen(p, t)) { s.pan.pan.cancelScheduledValues(0); this.param(s.pan.pan, 0); }   // Panorama ist eingefroren mitgerechnet
      else if (!A('pan')) this.param(s.pan.pan, t.pan); else if (!auto.playing) this.param(s.pan.pan, at('pan'));
      for (const b of busses) {
        const key = `send:${b.id}`, sa = A(key);
        const lvl = t.sends.find((x) => x.busId === b.id)?.level ?? 0;
        const g = sa || lvl > 0 ? this.send(s, b.id) : s.sends.get(b.id);
        if (!g) continue;
        if (!sa) this.param(g.gain, lvl); else if (!auto.playing) this.param(g.gain, autoLin(at(key)));
      }
      for (const [bid, g] of s.sends) if (!busses.some((b) => b.id === bid)) { g.disconnect(); s.sends.delete(bid); }
    }
    for (const [id, s] of this.strips) if (!p.tracks.some((t) => t.id === id)) { s.out.disconnect(); s.chain.dispose(); (s.pan as Panner).dispose?.(); s.sends.forEach((g) => g.disconnect()); this.strips.delete(id); }
    // Master
    const mb = p.mixBus;
    this.param(this.legacyComp.threshold, mb?.threshold ?? 0);
    this.legacyComp.ratio.value = mb?.ratio ?? 1;
    this.legacyEq.frequency.value = mb?.eqF ?? 300;
    this.param(this.legacyEq.gain, mb?.eqG ?? 0);
    const mt = masterTrack(p);
    this.masterChain.sync(p.masterInserts ?? [], env, insOverrides(mt, auto.pos, auto.manual));
    // Master-Automation (Lautstärke): während der Wiedergabe eingeplant, im Stillstand der Wert an der Position
    if (!isAutomated(mt, 'volume') || auto.manual.has('master|volume')) this.param(this.masterFader.gain, dbToGain(p.masterDb));
    else if (!auto.playing) this.param(this.masterFader.gain, autoLin(valueAt(points(mt, 'volume'), auto.pos, 'volume', 0)));
    this.phoneHP.frequency.value = 450; this.phoneLP.frequency.value = 4500;
    this.route(!!mb && (mb.ratio > 1 || mb.eqG !== 0), monitor.phone);
    this.monoNode.channelCount = monitor.mono ? 1 : 2;
    this.monoNode.channelCountMode = monitor.mono ? 'explicit' : 'max';
  }

  private send(s: StripNodes, busId: string): GainNode {
    let g = s.sends.get(busId);
    if (!g) { g = this.ctx.createGain(); g.gain.value = 0; s.fader.connect(g).connect(this.bus(busId).input); s.sends.set(busId, g); }
    return g;
  }

  /** Plugin-Parameter-Automation an Position pos anwenden (Wiedergabe: alle 25 ms; Export: über suspend) */
  applyInsAutomation(p: Project, pos: number, manual: Set<string>) {
    const env: PluginEnv = { bpm: p.bpm, offline: !this.live };
    for (const t of p.tracks) {
      if (t.kind === 'bus') continue;
      const ov = insOverrides(t, pos, manual), s = this.strips.get(t.id);
      if (ov && s && !isFrozen(p, t)) s.chain.sync(browserInserts(p, t, this.live), env, ov);
    }
    const mt = masterTrack(p), ov = insOverrides(mt, pos, manual);
    if (ov) this.masterChain.sync(p.masterInserts ?? [], env, ov);
  }

  /** Automationskurven ab `from` auf die AudioParams legen (live und beim Export) */
  scheduleAutomation(p: Project, from: number, toCtx: (t: number) => number, manual: Set<string>) {
    const now = this.ctx.currentTime;
    const mt = masterTrack(p);
    for (const t of [...p.tracks, mt]) {
      if (t.kind === 'bus' && t.id !== 'master') continue;
      const s = t.id === 'master' ? this.master : this.strips.get(t.id);
      if (!s) continue;
      const targets: [string, AudioParam, (v: number) => number][] = t.id === 'master' ? [['volume', this.masterFader.gain, autoLin]] : [
        ['volume', s.autoGain.gain, autoLin], ['pan', s.pan.pan, (v) => v], ['mute', s.muteGain.gain, (v) => (v >= 0.5 ? 0 : 1)],
        ...Object.keys(t.automation?.lanes ?? {}).filter((k) => k.startsWith('send:')).map((k) => [k, this.send(s, k.slice(5)).gain, autoLin] as [string, AudioParam, (v: number) => number]),
      ];
      const fz = t.id !== 'master' && isFrozen(p, t);
      for (const [param, ap, conv] of targets) {
        if (!isAutomated(t, param) || manual.has(`${t.id}|${param}`)) continue;
        if (fz && param === 'pan') continue;   // eingefroren: Panorama ist in der gerechneten Fassung
        ap.cancelScheduledValues(now);
        const sm = samples(points(t, param), param, from);
        ap.setValueAtTime(conv(sm[0].v), Math.max(now, toCtx(from)));
        for (const x of sm.slice(1)) {
          const when = toCtx(x.t);
          if (when <= now) continue;
          if (param === 'mute') ap.setValueAtTime(conv(x.v), when); else ap.linearRampToValueAtTime(conv(x.v), when);
        }
      }
    }
  }
  /** Eingeplante Automation verwerfen (Stopp, Neuplanung) */
  cancelAutomation() {
    const now = this.ctx.currentTime;
    if (this.masterFader) this.masterFader.gain.cancelScheduledValues(now);
    for (const s of this.strips.values()) { s.autoGain.gain.cancelScheduledValues(now); s.muteGain.gain.cancelScheduledValues(now); s.pan.pan.cancelScheduledValues(now); for (const g of s.sends.values()) g.gain.cancelScheduledValues(now); }
  }

  /** Spitzenpegel (linear) links/rechts eines Kanals */
  stereoPeak(id: string): [number, number] {
    const s = this.channel(id);
    if (!s) return [0, 0];
    const pk = (a: AnalyserNode, b: Float32Array) => { a.getFloatTimeDomainData(b as Float32Array<ArrayBuffer>); let m = 0; for (let i = 0; i < b.length; i++) { const v = b[i] < 0 ? -b[i] : b[i]; if (v > m) m = v; } return m; };
    return [pk(s.aL, s.bufL), pk(s.aR, s.bufR)];
  }
  resetLoudness() { this.loud?.port.postMessage('reset'); this.loudness = { m: -Infinity, s: -Infinity, i: -Infinity, peak: -Infinity, maxPeak: -Infinity }; }
}
